// Itens da nota: busca produto cadastrado ou digita livre; ao informar o NCM,
// o sistema sugere CFOP e alíquotas (ICMS/PIS/COFINS) que o usuário pode ajustar.
import React, { useState } from "react";
import { fiscal } from "../api";
import { brl, round2, soDigitos } from "../utils";
import { Aviso, Botao, Campo, Entrada, Modal, Numero, Selecao, Valor } from "./ui";
import Tributos from "./Tributos";

const ITEM_VAZIO = {
  codigo: "", descricao: "", ncm: "", cest: "", gtin: "", unidade: "UN",
  quantidade: 1, valor_unitario: 0, desconto: 0, tributos: null,
};

export const valorItem = (i) => round2(Number(i.quantidade || 0) * Number(i.valor_unitario || 0) - Number(i.desconto || 0));

export default function Itens({ itens, onChange, contexto, devolucao = false }) {
  const [editando, setEditando] = useState(null); // índice ou "novo"
  const [rascunho, setRascunho] = useState(ITEM_VAZIO);

  const abrir = (idx) => {
    setRascunho(idx === "novo" ? { ...ITEM_VAZIO } : { ...itens[idx] });
    setEditando(idx);
  };
  const salvar = (item) => {
    const lista = [...itens];
    if (editando === "novo") lista.push(item);
    else lista[editando] = item;
    onChange(lista);
    setEditando(null);
  };
  const remover = (idx) => onChange(itens.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <p className="text-sm text-gray-600">{itens.length} item(ns)</p>
        <Botao onClick={() => abrir("novo")}>+ Adicionar item</Botao>
      </div>
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Produto</th>
              <th className="px-3 py-2">NCM</th>
              <th className="px-3 py-2">CFOP</th>
              <th className="px-3 py-2 text-right">Qtd</th>
              <th className="px-3 py-2 text-right">Unitário</th>
              <th className="px-3 py-2 text-right">Desconto</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y">
            {itens.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  Nenhum item. Clique em “Adicionar item”.
                </td>
              </tr>
            )}
            {itens.map((i, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-3 py-2">{idx + 1}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{i.descricao}</div>
                  <div className="text-xs text-gray-500">{i.codigo}</div>
                  {devolucao && (i.item_referenciado
                    ? <div className="text-xs text-gray-500" data-testid="item-ref">Item {i.item_referenciado} da nota original</div>
                    : <div className="text-xs text-amber-700" data-testid="item-ref-falta">Falta o nº do item na nota original</div>)}
                </td>
                <td className="px-3 py-2">{i.ncm}</td>
                <td className="px-3 py-2">{i.tributos?.cfop}</td>
                <td className="px-3 py-2 text-right tabular-nums">{i.quantidade}</td>
                <td className="px-3 py-2 text-right tabular-nums">{brl(i.valor_unitario)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{brl(i.desconto)}</td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">{brl(valorItem(i))}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <Botao variante="fantasma" onClick={() => abrir(idx)}>Editar</Botao>
                  <Botao variante="fantasma" className="text-red-600" onClick={() => remover(idx)}>Remover</Botao>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal aberto={editando !== null} titulo={editando === "novo" ? "Novo item" : "Editar item"} onFechar={() => setEditando(null)} largura="max-w-3xl">
        {editando !== null && (
          <EditorItem inicial={rascunho} contexto={contexto} devolucao={devolucao} onSalvar={salvar} onCancelar={() => setEditando(null)} />
        )}
      </Modal>
    </div>
  );
}

function EditorItem({ inicial, contexto, devolucao = false, onSalvar, onCancelar }) {
  const [i, setI] = useState(inicial);
  const [busca, setBusca] = useState("");
  const [resultados, setResultados] = useState([]);
  const [sugerindo, setSugerindo] = useState(false);
  const [erro, setErro] = useState(null);
  const set = (campo, v) => setI((x) => ({ ...x, [campo]: v }));

  const buscarProdutos = async (texto) => {
    setBusca(texto);
    if (texto.length < 2) return setResultados([]);
    try {
      setResultados(await fiscal.produtos(texto));
    } catch {
      setResultados([]);
    }
  };

  const sugerir = async (ncm = i.ncm) => {
    const n = soDigitos(ncm);
    if (n.length !== 8) return;
    setSugerindo(true);
    setErro(null);
    try {
      const t = await fiscal.sugerirTributos({ ncm: n, ...contexto });
      setI((x) => ({ ...x, ncm: n, tributos: t }));
    } catch (e) {
      setErro(e.message);
    } finally {
      setSugerindo(false);
    }
  };

  const escolher = (p) => {
    setI((x) => ({
      ...x, produto_id: p.id, codigo: p.codigo, descricao: p.descricao, ncm: p.ncm, cest: p.cest || "",
      gtin: p.gtin || "", unidade: p.unidade || "UN", valor_unitario: p.preco || 0,
    }));
    setResultados([]);
    setBusca("");
    sugerir(p.ncm); // CFOP depende da operação, então recalcula mesmo com tributos salvos
  };

  // Código é opcional na tela: se ficar vazio, usa o GTIN ou "ITEM" (o campo cProd da NF-e não precisa ser único)
  const faltando = [
    !i.descricao && "descrição",
    soDigitos(i.ncm).length !== 8 && "NCM com 8 dígitos",
    !(i.quantidade > 0) && "quantidade",
    !(i.valor_unitario > 0) && "valor unitário",
    !i.tributos && "tributos (aguarde a sugestão pelo NCM)",
    devolucao && !(Number(i.item_referenciado) >= 1 && Number(i.item_referenciado) <= 990) && "nº do item na nota original",
  ].filter(Boolean);
  const valido = faltando.length === 0;
  const confirmar = () => onSalvar({ ...i, codigo: (i.codigo || "").trim() || soDigitos(i.gtin) || "ITEM" });

  return (
    <div className="space-y-4">
      <div className="relative">
        <Campo rotulo="Buscar produto cadastrado">
          <Entrada value={busca} onChange={(e) => buscarProdutos(e.target.value)} placeholder="Código, descrição ou código de barras" />
        </Campo>
        {resultados.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-white shadow border-gray-200">
            {resultados.map((p) => (
              <li key={p.id}>
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50" onClick={() => escolher(p)}>
                  <span className="font-medium">{p.descricao}</span>{" "}
                  <span className="text-gray-500">· {p.codigo} · NCM {p.ncm} · {brl(p.preco)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Campo rotulo="Código (opcional)" className="md:col-span-1">
          <Entrada value={i.codigo} onChange={(e) => set("codigo", e.target.value)} />
        </Campo>
        <Campo rotulo="Descrição" className="col-span-2 md:col-span-5">
          <Entrada value={i.descricao} onChange={(e) => set("descricao", e.target.value)} maxLength={120} />
        </Campo>
        <Campo rotulo="NCM" dica={sugerindo ? "Buscando tributos…" : "8 dígitos — tributos sugeridos automaticamente"} className="md:col-span-2">
          <Entrada value={i.ncm} maxLength={10} onChange={(e) => set("ncm", soDigitos(e.target.value))} onBlur={() => sugerir()} />
        </Campo>
        <Campo rotulo="CEST" className="md:col-span-1">
          <Entrada value={i.cest || ""} onChange={(e) => set("cest", soDigitos(e.target.value))} maxLength={7} />
        </Campo>
        <Campo rotulo="GTIN / EAN" className="md:col-span-2">
          <Entrada value={i.gtin || ""} onChange={(e) => set("gtin", soDigitos(e.target.value))} maxLength={14} />
        </Campo>
        <Campo rotulo="Unidade" className="md:col-span-1">
          <Selecao value={i.unidade} onChange={(e) => set("unidade", e.target.value)}
            opcoes={["UN", "PC", "KG", "G", "L", "ML", "M", "M2", "M3", "CX", "PCT", "DZ", "PAR", "SC", "FD"].map((u) => [u, u])} />
        </Campo>
        <Campo rotulo="Quantidade" className="md:col-span-2">
          <Numero value={i.quantidade} onChange={(v) => set("quantidade", Number(v))} />
        </Campo>
        <Campo rotulo="Valor unitário" className="md:col-span-2">
          <Valor value={i.valor_unitario} onChange={(v) => set("valor_unitario", v)} />
        </Campo>
        <Campo rotulo="Desconto" className="md:col-span-2">
          <Valor value={i.desconto} onChange={(v) => set("desconto", v)} />
        </Campo>
        {devolucao && (
          <Campo rotulo="Nº do item na nota original" dica="Posição do item na nota que está sendo devolvida (1, 2, 3…)" className="md:col-span-2">
            <Entrada inputMode="numeric" maxLength={3} value={i.item_referenciado ?? ""} data-testid="item-referenciado-input"
              onChange={(e) => { const d = soDigitos(e.target.value).slice(0, 3); set("item_referenciado", d ? Number(d) : null); }} />
          </Campo>
        )}
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {i.tributos ? (
        <Tributos valor={i.tributos} onChange={(t) => set("tributos", t)} regime={contexto.regime} />
      ) : (
        <Aviso>Informe o NCM para o sistema sugerir CFOP e alíquotas.</Aviso>
      )}

      <div className="flex items-center justify-between border-t pt-4 border-gray-200">
        <span className="text-sm">
          Total do item: <strong>{brl(valorItem(i))}</strong>
          {!valido && <span className="block text-xs text-amber-700">Falta: {faltando.join(", ")}</span>}
        </span>
        <div className="flex gap-2">
          <Botao variante="secundario" onClick={onCancelar}>Cancelar</Botao>
          <Botao disabled={!valido} onClick={confirmar}>Confirmar item</Botao>
        </div>
      </div>
    </div>
  );
}
